// Normalização usada pela regra de divergência de nível CRM (sync-level).
//
// As duas pontas comparadas não falam a mesma língua: o core guarda um slug
// ("nivel_12") e a Smartico entrega o NOME do degrau ("NÍVEL 12", com acento e
// às vezes um sufixo depois de " - "). Passar os dois pelo mesmo funil é o que
// torna a comparação possível sem depender do formato exato de nenhum lado —
// se o core mudar o separador ou a Smartico mudar a caixa, os dois continuam
// caindo no mesmo slug.
export function normalizeCrmLevelSlug(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const slug = value
    // O nome da Smartico pode vir composto ("NÍVEL 12 - Bronze"): só o trecho
    // antes do separador identifica o degrau — é a mesma leitura que o
    // `currentUserLevelName` da store já faz pra exibição.
    .split(' - ')[0]!
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return slug || null;
}

// Rótulo de exibição pra um slug CRM, na língua da trilha de níveis do site:
// 'BRONZE LV 1' → 'Bronze 1', 'PRATA LV 5' → 'Prata 5', 'NIVEL 0' → 'Nível 0',
// 'VIP 3' → 'VIP 3', 'SIGNATURE' → 'Signature'. É a copy do aviso de nível
// (BaseLevelGateNotice), que lê o degrau mínimo do payload do /players/me — a
// régua é editável no backoffice, então o nome NÃO pode vir de constante local.
//
// `null` pra qualquer coisa que não seja string não vazia: quem chama troca o
// nome por uma frase genérica em vez de mostrar "undefined".
export function crmLevelLabel(slug: unknown): string | null {
  if (typeof slug !== 'string') return null;

  const trimmed = slug.trim().replace(/\s+/g, ' ');

  if (!trimmed) return null;

  const rung = /^(.+?)\s+lv\s+(\d+)$/i.exec(trimmed);

  if (rung) return `${capitalize(rung[1]!)} ${rung[2]}`;

  if (/^nivel\s+\d+$/i.test(trimmed)) {
    return `Nível ${trimmed.replace(/\D+/g, '')}`;
  }

  if (/^vip\b/i.test(trimmed)) return trimmed.toUpperCase();

  return capitalize(trimmed);
}

// Chave do elo (LEVEL_TIERS em utils/levels) que um slug CRM referencia:
// 'BRONZE LV 1' → 'bronze', 'PRATA LV 5' → 'prata'. Sem acento e em minúsculas,
// que é como as chaves estão escritas lá. `null` quando o slug não nomeia um
// elo (NIVEL 0, VIP, lixo) — quem chama escolhe o fallback de arte.
export function crmLevelTierKey(slug: unknown): string | null {
  if (typeof slug !== 'string') return null;

  const name = slug.trim().split(/\s+lv\s+/i)[0];

  if (!name) return null;

  const key = name
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .trim();

  return key || null;
}

function capitalize(value: string): string {
  const lower = value.toLowerCase();

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Título do aviso de nível nos modais, em texto puro: "Disponível a partir do
// nível Bronze 1." É o FALLBACK da `message` do BaseNoticeSheet — quando há
// `levelGate`, a folha desenha o mesmo título ela mesma, com o trecho do nível
// destacado na cor do elo. Sem o nome do degrau (core não mandou), a frase fica
// genérica em vez de inventar um.
export function levelGateMessage(minLevel: unknown): string {
  const label = crmLevelLabel(minLevel);

  return label
    ? `Disponível a partir do nível ${label}.`
    : 'Disponível a partir de um nível mínimo.';
}
